import type { AuditItemDto } from '@glory/contracts';
import { formatDateTime, t } from '@glory/i18n';
import {
  Badge,
  Button,
  dataColumns,
  DataTable,
  DateRangeField,
  EmptyState,
  HybridPanel,
  PageHeader,
  SelectField,
} from '@glory/ui';
import { fromIsoDate, isIsoDate } from '@glory/ui/fields/parsers';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ScrollText } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import { useState } from 'react';

import { ChangeList } from '../../../features/audit/change-list.tsx';
import { EntityHistory } from '../../../features/audit/entity-history.tsx';
import {
  actionLabel,
  AUDIT_ENTITIES,
  auditActions,
  entityCaption,
  entityLabel,
} from '../../../features/audit/format.ts';
import { orpc } from '../../../lib/orpc.ts';
import { PersonAvatar } from '../../../lib/people.tsx';
import { requirePermissionRoute } from '../../../lib/permissions.ts';

/** Фильтры журнала живут в адресе: отфильтрованный вид можно переслать. */
interface AuditSearch {
  readonly actor?: string;
  readonly entity?: string;
  readonly action?: string;
  readonly from?: string;
  readonly to?: string;
}

const text = (value: unknown) => (typeof value === 'string' && value !== '' ? value : undefined);
const date = (value: unknown) => (typeof value === 'string' && isIsoDate(value) ? value : undefined);

export const Route = createFileRoute('/_app/admin/audit')({
  validateSearch: (search: Record<string, unknown>): AuditSearch => {
    const parsed = {
      actor: text(search.actor),
      entity: text(search.entity),
      action: text(search.action),
      from: date(search.from),
      to: date(search.to),
    };
    return Object.fromEntries(Object.entries(parsed).filter(([, v]) => v !== undefined));
  },
  beforeLoad: requirePermissionRoute('audit.read.all'),
  component: AuditPage,
});

/** Начало следующего дня по местному времени. */
function nextDay(value: string): Date {
  const day = fromIsoDate(value);
  return new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
}

const col = dataColumns<AuditItemDto>();
const columns = col.columns([
  col.accessor('at', {
    header: t('audit.columnAt'),
    meta: { width: '9.5rem' },
    cell: (c) => <span className="text-small tabular-nums">{formatDateTime(c.getValue())}</span>,
  }),
  col.accessor((item) => item.actor?.name ?? t('audit.system'), {
    id: 'actor',
    header: t('audit.columnActor'),
    meta: { width: '1fr', hideOnMobile: true, noClip: true },
    cell: (c) => {
      const { actor } = c.row.original;
      return (
        <span className="flex min-w-0 items-center gap-1.5">
          {actor ? <PersonAvatar person={actor} /> : null}
          <span className="truncate">{c.getValue()}</span>
        </span>
      );
    },
  }),
  col.accessor('action', {
    header: t('audit.columnAction'),
    meta: { width: '1.2fr' },
    cell: (c) => <span className="font-semibold">{actionLabel(c.getValue())}</span>,
  }),
  col.display({
    id: 'entity',
    header: t('audit.columnEntity'),
    meta: { width: '1.3fr', hideOnMobile: true },
    cell: (c) => (
      <span className="flex min-w-0 items-center gap-1.5">
        <Badge>{entityLabel(c.row.original.entityType)}</Badge>
        <span className="truncate">{entityCaption(c.row.original)}</span>
      </span>
    ),
  }),
  col.display({
    id: 'changes',
    header: t('audit.columnChanges'),
    meta: { width: '2fr', hideOnMobile: true },
    cell: (c) => <ChangeList changes={c.row.original.changes} limit={2} />,
  }),
]);

function AuditPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [openId, setOpenId] = useState<string | null>(null);
  const users = useQuery(orpc.identity.users.list.queryOptions());

  const filter = (patch: Partial<AuditSearch>) => {
    setOpenId(null);
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  };

  const audit = useInfiniteQuery(
    orpc.audit.list.infiniteOptions({
      input: (cursor: string | undefined) => ({
        actorId: search.actor,
        entityType: search.entity,
        action: search.action,
        from: search.from ? fromIsoDate(search.from) : undefined,
        // Конец периода включительно: до начала следующего дня.
        to: search.to ? nextDay(search.to) : undefined,
        cursor,
      }),
      initialPageParam: undefined,
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    }),
  );
  const items = audit.data?.pages.flatMap((page) => page.items) ?? [];
  const openIndex = items.findIndex((item) => item.id === openId);
  const open = openIndex >= 0 ? items[openIndex] : undefined;
  const filtered = Object.keys(search).length > 0;

  return (
    <div className="relative flex min-h-full w-full flex-col gap-4 px-4 py-5 md:px-6">
      <PageHeader title={t('audit.title')} />
      <p className="-mt-2 max-w-[70ch] text-muted">{t('audit.lead')}</p>
      <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SelectField
          label={t('audit.filterActor')}
          placeholder={t('audit.anyone')}
          clearable
          value={search.actor ?? null}
          options={(users.data ?? []).map((u) => ({ value: u.id, label: u.name, description: u.email }))}
          onValueChange={(actor) => {
            filter({ actor: actor ?? undefined });
          }}
        />
        <SelectField
          label={t('audit.filterEntity')}
          placeholder={t('audit.anyone')}
          clearable
          value={search.entity ?? null}
          options={AUDIT_ENTITIES.map((type) => ({ value: type, label: entityLabel(type) }))}
          onValueChange={(entity) => {
            // Действие другого объекта теряет смысл — сбрасываем.
            const keep = entity === null || search.action?.startsWith(`${entity}.`);
            filter({ entity: entity ?? undefined, ...(keep ? {} : { action: undefined }) });
          }}
        />
        <SelectField
          label={t('audit.filterAction')}
          placeholder={t('audit.anyone')}
          clearable
          value={search.action ?? null}
          options={auditActions(search.entity ?? null).map((action) => ({
            value: action,
            label: actionLabel(action),
          }))}
          onValueChange={(action) => {
            filter({ action: action ?? undefined });
          }}
        />
        <DateRangeField
          label={t('audit.filterPeriod')}
          value={search.from && search.to ? { from: search.from, to: search.to } : null}
          onValueChange={(range) => {
            filter({ from: range?.from, to: range?.to });
          }}
        />
      </div>
      {filtered ? (
        <Button
          size="sm"
          variant="ghost"
          className="-mt-1 self-start"
          onClick={() => {
            setOpenId(null);
            void navigate({ search: {}, replace: true });
          }}
        >
          {t('audit.resetFilters')}
        </Button>
      ) : null}

      <DataTable
        aria-label={t('audit.title')}
        data={items}
        columns={columns}
        getRowId={(item) => item.id}
        loading={audit.isPending}
        activeRowId={openId}
        onRowActivate={(item) => {
          setOpenId(item.id);
        }}
        empty={
          <EmptyState icon={<ScrollText />} title={filtered ? t('audit.emptyFiltered') : t('audit.empty')} />
        }
      />
      {audit.hasNextPage ? (
        <Button
          variant="secondary"
          className="self-center"
          loading={audit.isFetchingNextPage}
          onClick={() => void audit.fetchNextPage()}
        >
          {t('audit.loadMore')}
        </Button>
      ) : null}

      <AnimatePresence initial={false}>
        {open ? (
          <HybridPanel
            key="panel"
            title={entityCaption(open)}
            meta={<Badge>{entityLabel(open.entityType)}</Badge>}
            onClose={() => {
              setOpenId(null);
            }}
            onPrevious={() => {
              const previous = items[openIndex - 1];
              if (previous) setOpenId(previous.id);
            }}
            onNext={() => {
              const next = items[openIndex + 1];
              if (next) setOpenId(next.id);
            }}
          >
            <EntityHistory entityType={open.entityType} entityId={open.entityId} />
          </HybridPanel>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
